<template>
  <b-modal
    ref="modal"
    title="Xóa hàng loạt hóa đơn nháp"
    size="lg"
    no-close-on-backdrop
    :hide-header-close="isProcessing"
    :no-close-on-esc="isProcessing"
    hide-footer
    @hidden="onHidden"
  >
    <div class="bulk-delete-shell">
      <BulkDeleteOverview :stats="overviewStats" />

       <BulkIssueValidationControl
        v-if="phase === 'review'"
        :active-view="activeView"
        :eligible-count="eligibleCount"
        :blocked-count="blockedCount"
        @change="setActiveView"
      />

      <div class="bulk-delete-shell__surface">
        <BulkDeleteEligibleTable
          v-if="phase === 'review' && activeView === 'eligible'"
          :items="eligibleItems"
          :selected-ids="selectedEligibleIds"
          :format-currency="formatCurrency"
          @toggle="toggleEligible"
          @select-all="selectAllEligible"
          @deselect-all="deselectAllEligible"
        />

        <BulkDeleteBlockedTable
          v-else-if="phase === 'review' && activeView === 'blocked'"
          :items="blockedItems"
        />

        <BulkDeleteBlockedTable
          v-else-if="phase === 'zeroEligible'"
          :items="blockedItems"
        />

        <BulkDeleteProcessingState
          v-else-if="phase === 'processing'"
          :progress-percent="progressPercent"
          :processed="progress.processed"
          :total="progress.total"
          :current-item-label="progress.currentItem || ''"
        />

        <div
          v-else-if="phase === 'result'"
          class="bulk-delete-shell__result"
        >
          <BulkDeleteResultState :result="result" />

          <div
            v-if="result.failed.length > 0"
            class="bulk-delete-shell__result-actions"
          >
             <b-button
              size="sm"
              variant="outline-warning"
              @click="retryFailed"
              > <i class="fas fa-redo mr-1"></i> Thử lại {{ result.failed.length }} HĐ thất bại
              </b-button
            >
          </div>
        </div>
      </div>

       <BulkIssueActionFooter
        v-if="showFooter"
        :summary="footerSummary"
        :cancel-label="footerCancelLabel"
        :primary-label="footerPrimaryLabel"
        :primary-disabled="footerPrimaryDisabled"
        :show-primary="showFooterPrimary"
        @cancel="handleFooterCancel"
        @confirm="handleFooterConfirm"
      />
    </div>
  </b-modal>
</template>

<script>
  import { mapGetters } from 'vuex'
  import Swal from 'sweetalert2'
  import ApiService from '@/core/services/api.service'
  import {
    EINVOICE_BULK_DELETE_DRAFT,
    EINVOICE_FETCH_LIST,
  } from '@/core/services/store/modules/einvoice/einvoice.module'
  import { buildDeleteDraftEndpoint } from '@/core/services/store/modules/einvoice/einvoice.contract'
  import einvoiceMixin from '../mixins/einvoice.mixin'
  import BulkIssueActionFooter from './BulkIssueActionFooter.vue'
  import BulkIssueValidationControl from './BulkIssueValidationControl.vue'
  import BulkDeleteOverview from './BulkDeleteOverview.vue'
  import BulkDeleteEligibleTable from './BulkDeleteEligibleTable.vue'
  import BulkDeleteBlockedTable from './BulkDeleteBlockedTable.vue'
  import BulkDeleteProcessingState from './BulkDeleteProcessingState.vue'
  import BulkDeleteResultState from './BulkDeleteResultState.vue'
  import {
    classifyBulkDeleteItems,
    buildBulkDeleteOverviewStats,
    buildBulkDeleteDecisionSummary,
    buildBulkDeleteFooterSummary,
    executeBulkDelete,
  } from './bulkDeleteModal.logic'

  export default {
    name: 'BulkDeleteModal',
    components: {
      BulkIssueActionFooter,
      BulkIssueValidationControl,
      BulkDeleteOverview,
      BulkDeleteEligibleTable,
      BulkDeleteBlockedTable,
      BulkDeleteProcessingState,
      BulkDeleteResultState,
    },
    mixins: [einvoiceMixin],
    data() {
      return {
        activeView: 'eligible',           // 'eligible' | 'blocked'
        selectedEligibleIds: [],           // String[] of salesInvoiceId
        isExecuting: false,               // guard against double-submit
      }
    },
    computed: {
      ...mapGetters(['einvoiceBulkDelete']),

      phase() {
        return this.einvoiceBulkDelete.phase || 'idle'
      },

      eligibleItems() {
        return this.einvoiceBulkDelete.eligibleItems || []
      },

      blockedItems() {
        return this.einvoiceBulkDelete.blockedItems || []
      },

      saleType() {
        return this.einvoiceBulkDelete.saleType || null
      },

      progress() {
        return (
          this.einvoiceBulkDelete.progress || {
            processed: 0,
            total: 0,
            currentItem: null,
          }
        )
      },

      result() {
        return this.einvoiceBulkDelete.result || { succeeded: [], failed: [] }
      },

      isProcessing() {
        return this.phase === 'processing'
      },

      eligibleCount() {
        return this.eligibleItems.length
      },

      blockedCount() {
        return this.blockedItems.length
      },

      totalSelected() {
        return this.eligibleCount + this.blockedCount
      },

      selectedEligibleCount() {
        return this.selectedEligibleIds.length
      },

      progressPercent() {
        if (!this.progress.total) return 0
        return Math.round((this.progress.processed / this.progress.total) * 100)
      },

      overviewStats() {
        return buildBulkDeleteOverviewStats({
          totalSelected: this.totalSelected,
          eligibleCount: this.eligibleCount,
          blockedCount: this.blockedCount,
        })
      },

      showFooter() {
        return ['review', 'zeroEligible', 'error', 'result'].includes(this.phase)
      },

      footerSummary() {
        if (this.phase === 'review') {
          return buildBulkDeleteDecisionSummary({
            eligibleCount: this.selectedEligibleCount,
            blockedCount: this.blockedCount,
          })
        }

        return buildBulkDeleteFooterSummary(this.phase, this.result)
      },

      footerCancelLabel() {
        return this.phase === 'result' ? 'Đóng và làm mới danh sách' : 'Hủy'
      },

      footerPrimaryLabel() {
        return `Xóa ${this.selectedEligibleCount} hóa đơn nháp`
      },

      footerPrimaryDisabled() {
        return this.phase !== 'review' || this.selectedEligibleCount === 0 || this.isExecuting
      },

      showFooterPrimary() {
        return this.phase === 'review'
      },
    },
    methods: {
      /**
       * Reset local component state (not Vuex state).
       */
      resetLocalState() {
        this.activeView = 'eligible'
        this.selectedEligibleIds = []
        this.isExecuting = false
      },

      /**
       * Public entry point called from List.vue.
       * Classifies items, dispatches to Vuex, then shows the modal.
       *
       * @param {Object} payload
       * @param {Array} payload.selectedRows - Raw selected rows
       * @param {string} payload.saleType - 'retail' | 'wholesale'
       */
      async open({ selectedRows = [], saleType } = {}) {
        this.resetLocalState()

        const { eligible, blocked } = classifyBulkDeleteItems(selectedRows)

        await this.$store.dispatch(EINVOICE_BULK_DELETE_DRAFT, {
          eligible,
          blocked,
          saleType,
        })

        this.selectedEligibleIds = eligible.map((item) => String(item.salesInvoiceId))
        this.activeView = eligible.length > 0 ? 'eligible' : 'blocked'
        this.$refs.modal.show()
      },

      /**
       * Execute the bulk delete operation.
       * Guards against double-submit via `isExecuting`.
       * Shows a Swal confirmation before proceeding.
       */
      async confirmDelete() {
        if (this.isExecuting || this.selectedEligibleCount === 0) return

        const confirmed = await Swal.fire({
          title: `Xác nhận xóa ${this.selectedEligibleCount} hóa đơn nháp?`,
          text: 'Các hóa đơn nháp sẽ bị xóa vĩnh viễn. Hành động này không thể hoàn tác.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Xóa',
          cancelButtonText: 'Hủy',
          confirmButtonColor: '#dc3545',
          cancelButtonColor: '#E5EAEE',
          reverseButtons: true,
        })

        if (!confirmed.isConfirmed) return

        this.isExecuting = true
        this.$store.commit('SET_BULK_DELETE_PHASE', 'processing')
        this.$store.commit('SET_BULK_DELETE_PROGRESS', {
          processed: 0,
          total: this.selectedEligibleCount,
          currentItem: null,
        })

        const itemsToDelete = this.eligibleItems.filter((item) =>
          this.selectedEligibleIds.includes(String(item.salesInvoiceId)),
        )

        try {
          const result = await executeBulkDelete(
            itemsToDelete,
            this.saleType,
            {
              onProgress: (progress) => {
                this.$store.commit('SET_BULK_DELETE_PROGRESS', progress)
              },
              onItemComplete: () => {},
            },
            ApiService,
            buildDeleteDraftEndpoint,
          )

          this.$store.commit('SET_BULK_DELETE_RESULT', result)
          this.$store.commit('SET_BULK_DELETE_PHASE', 'result')
        } catch (err) {
          this.$store.commit('SET_BULK_DELETE_ERROR', err?.message || 'Lỗi không xác định')
          this.$store.commit('SET_BULK_DELETE_PHASE', 'error')
        } finally {
          this.isExecuting = false
        }
      },

      /**
       * Reset back to review phase with failed items as the new eligible list.
       * Used by the "Thử lại" button.
       */
      retryFailed() {
        const failedItems = this.result.failed
        if (!failedItems.length) return

        this.resetLocalState()

        const retryEligible = failedItems.map((f) => f.item)

        this.$store.commit('SET_BULK_DELETE_ELIGIBLE', {
          eligible: retryEligible,
          blocked: [],
          saleType: this.saleType,
        })

        this.selectedEligibleIds = retryEligible.map((item) => String(item.salesInvoiceId))
        this.$store.commit('SET_BULK_DELETE_RESULT', { succeeded: [], failed: [] })
        this.$store.commit('SET_BULK_DELETE_PHASE', 'review')
        this.activeView = 'eligible'
      },

      /**
       * Close the modal and emit 'closed' so the parent can refresh.
       */
      closeAndRefresh() {
        this.$refs.modal.hide()
        this.$emit('closed')
      },

      /**
       * Hide modal (without triggering list refresh).
       */
      hide() {
        this.$refs.modal.hide()
      },

      /**
       * Handle the footer cancel/close button.
       * In result phase → close and refresh. Otherwise → just hide.
       */
      handleFooterCancel() {
        if (this.phase === 'result') {
          this.closeAndRefresh()
          return
        }

        this.hide()
      },

      /**
       * Handle the footer primary (confirm) button.
       */
      handleFooterConfirm() {
        this.confirmDelete()
      },

      /**
       * Called when the b-modal emits 'hidden' (after animation).
       * Always resets Vuex state — even if the user closed mid-flow.
       */
      onHidden() {
        this.resetLocalState()
        this.$store.commit('RESET_BULK_DELETE')
      },

      /**
       * Switch the active eligible/blocked tab view.
       * @param {'eligible' | 'blocked'} view
       */
      setActiveView(view) {
        this.activeView = view
      },

      /**
       * Toggle a single eligible item's selection state.
       * @param {Object} item
       */
      toggleEligible(item) {
        const salesInvoiceId = String(item.salesInvoiceId)
        const idx = this.selectedEligibleIds.findIndex((id) => id === salesInvoiceId)
        if (idx >= 0) {
          this.selectedEligibleIds.splice(idx, 1)
        } else {
          this.selectedEligibleIds.push(salesInvoiceId)
        }
      },

      /**
       * Select all eligible items.
       */
      selectAllEligible() {
        this.selectedEligibleIds = this.eligibleItems.map((item) => String(item.salesInvoiceId))
      },

      /**
       * Deselect all eligible items.
       */
      deselectAllEligible() {
        this.selectedEligibleIds = []
      },
    },
  }
</script>

<style scoped>
  .bulk-delete-shell {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-height: 420px;
  }

  .bulk-delete-shell__surface {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-height: 0;
  }

  .bulk-delete-shell__result {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .bulk-delete-shell__result-actions {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex-wrap: wrap;
  }

  @media (max-width: 767.98px) {
    .bulk-delete-shell__result-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .bulk-delete-shell__result-actions > * {
      margin-right: 0 !important;
      width: 100%;
    }
  }
</style>
